import { useState } from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { useNotifications, InAppNotification } from '../hooks/useNotifications';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { cn } from '../lib/cn';
import { formatDate } from '../lib/format';

/**
 * Bell with unread badge + dropdown panel. Realtime updates come from
 * the Socket.io gateway via `useNotifications`; the 60s background poll
 * is the catch-all if a socket frame is missed.
 */
export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const { data: user } = useCurrentUser();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Disable until we know we have a logged-in user — the bell only
  // appears in AppShell anyway, but the hook is defensive.
  const enabled = !!user?.id;
  const { items, unreadCount, loading, markRead, markAllRead } = useNotifications(enabled);

  function onClickItem(n: InAppNotification) {
    if (!n.is_read) void markRead(n.id);
    setOpen(false);
    if (n.action_url) navigate(n.action_url);
  }

  // Localize the title by notification `kind` when we have a translation
  // for it; the stored (English) title is the fallback. This keeps the
  // feed localizable without a schema/migration change — the body text
  // (e.g. the quoted comment) stays as authored.
  function titleFor(n: InAppNotification): string {
    const key = `notifications.kind.${n.kind}`;
    const translated = t(key);
    return translated === key ? n.title : translated;
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          className="relative p-2 rounded-full hover:bg-ink-100 dark:hover:bg-ink-800 transition-colors"
          aria-label={t('notifications.openLabel')}
        >
          <Bell size={16} />
          {unreadCount > 0 ? (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-warn-500 text-white text-[10px] font-semibold leading-[16px] text-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          ) : null}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          className="z-50 w-[360px] max-w-[calc(100vw-24px)] rounded-xl border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900 shadow-lg p-0 outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          <div className="px-4 py-3 flex items-center justify-between border-b border-ink-200 dark:border-ink-800">
            <div className="font-medium text-sm">{t('notifications.title')}</div>
            {unreadCount > 0 ? (
              <button
                onClick={() => markAllRead()}
                className="text-xs text-accent-700 hover:underline flex items-center gap-1"
              >
                <CheckCheck size={12} /> {t('notifications.markAllRead')}
              </button>
            ) : null}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="px-4 py-8 text-center text-sm text-ink-500">{t('common.loading')}</div>
            ) : items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-ink-500">{t('notifications.empty')}</div>
            ) : (
              <ul className="divide-y divide-ink-200 dark:divide-ink-800">
                {items.map((n) => (
                  <li key={n.id}>
                    <button
                      onClick={() => onClickItem(n)}
                      className={cn(
                        'w-full text-left px-4 py-3 hover:bg-ink-50 dark:hover:bg-ink-800 transition-colors flex gap-3',
                        !n.is_read && 'bg-accent-50/40 dark:bg-accent-900/10',
                      )}
                    >
                      <div className={cn('mt-1.5 h-2 w-2 rounded-full shrink-0', n.is_read ? 'bg-transparent' : 'bg-accent-500')} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{titleFor(n)}</div>
                        {n.body ? (
                          <div className="text-xs text-ink-500 mt-0.5 line-clamp-2">{n.body}</div>
                        ) : null}
                        <div className="text-[11px] text-ink-400 mt-1 num">
                          {formatDate(n.created_at, i18n.language)}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
